/**
 * Password Requirements Component (Gate F9)
 * 
 * Shared component showing real-time password validation status.
 * Used in: /account (change password), /reset-password, /invite (registration)
 */

import { Check, X } from "lucide-react";
import { validatePassword, PASSWORD_REQUIREMENT_LABELS, type PasswordRequirements } from "@/lib/password-validation";

interface PasswordRequirementsProps {
  password: string;
}

export function PasswordRequirementsChecklist({ password }: PasswordRequirementsProps) {
  const { requirements } = validatePassword(password);

  return (
    <div className="space-y-1.5 text-sm" data-testid="password-requirements">
      {(Object.keys(requirements) as Array<keyof PasswordRequirements>).map((key) => {
        const met = requirements[key];
        return (
          <div
            key={key}
            className={`flex items-center gap-2 ${
              met ? "text-green-600 dark:text-green-400" : "text-muted-foreground"
            }`}
            data-testid={`requirement-${key}`}
          >
            {met ? (
              <Check className="h-4 w-4" />
            ) : (
              <X className="h-4 w-4" />
            )}
            <span>{PASSWORD_REQUIREMENT_LABELS[key]}</span>
          </div>
        );
      })}
    </div>
  );
}

interface PasswordStrengthIndicatorProps {
  password: string;
  showLabel?: boolean;
}

export function PasswordStrengthIndicator({ password, showLabel = true }: PasswordStrengthIndicatorProps) {
  const { requirements } = validatePassword(password);
  const metCount = Object.values(requirements).filter(Boolean).length;
  const total = Object.keys(requirements).length;
  
  let strength: "weak" | "fair" | "good" | "strong" = "weak";
  let color = "bg-red-500";
  
  if (metCount === total) {
    strength = "strong";
    color = "bg-green-500";
  } else if (metCount >= 4) {
    strength = "good";
    color = "bg-green-400";
  } else if (metCount >= 2) {
    strength = "fair";
    color = "bg-yellow-500";
  }

  const strengthLabels = {
    weak: "Weak",
    fair: "Fair",
    good: "Good",
    strong: "Strong",
  };

  return (
    <div className="space-y-1">
      <div className="flex gap-1 h-1.5">
        {[...Array(total)].map((_, i) => (
          <div
            key={i}
            className={`flex-1 rounded-full transition-colors ${
              i < metCount ? color : "bg-muted"
            }`}
          />
        ))}
      </div>
      {showLabel && password.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Password strength: <span className="font-medium">{strengthLabels[strength]}</span>
        </p>
      )}
    </div>
  );
}
